import { Component, OnInit, Input, ChangeDetectorRef, OnChanges, SimpleChanges, ChangeDetectionStrategy, ViewEncapsulation, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { startWith, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectionList } from '@angular/material/list';
import { DOWN_ARROW, TAB, ESCAPE } from '@angular/cdk/keycodes';
import { MatExpansionPanel } from '@angular/material/expansion';

import { isNullOrEmpty } from '@awp/core/utils';
import { Destination, DestinationType } from '../destination-select/destination-select.component';

@Component({
  selector: 'ap-travel-destination-dialog',
  templateUrl: './travel-destination-dialog.component.html',
  styleUrls: ['./travel-destination-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TravelDestinationDialogComponent implements OnInit, OnChanges, AfterViewInit {

  DestinationType = DestinationType;

  formSearchControl = new FormControl();
  isFilterActive: boolean = false;
  places: Destination[] = [];
  popularList: Destination[] = [];
  public selectedValues: Destination[] = [];
  isPanelExpanded: boolean = false;

  @Input() destinations: Destination[] = [];
  @Input() selected: Destination[] = [];
  @Input() placeholder: string;
  @Input() headerText: string;
  @Input() footerText: string;
  @Input() groupLimit: number = 3;
  @Input() showSelected: false;

  @ViewChild('input', { static: true }) input: ElementRef<HTMLInputElement>;
  @ViewChild(MatExpansionPanel, { static: false }) expansionPanel: MatExpansionPanel;
  @ViewChild('selectionList', { static: false }) selectionList: MatSelectionList;

  constructor(private changeDetectorRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.formSearchControl.valueChanges.pipe(
      startWith(null),
      debounceTime(300),
      distinctUntilChanged(),
      map((value: string | null) => {
        this.isFilterActive = !isNullOrEmpty(value);
        if (this.isPanelExpanded && this.isFilterActive) {
          this.expansionPanel.close();
        }

        if (this.isFilterActive) {
          return this.applyFilter(value).sort(this.sortList);
        } else {
          return this.applyPopularFilter().sort(this.sortList);
        }
      }),
      untilDestroyed(this)
    )
    .subscribe(destinations => {
      this.places = destinations;
      this.changeDetectorRef.markForCheck();
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.destinations != null && changes.destinations.currentValue) {
      this.popularList = this.buildPopularList(changes.destinations.currentValue);
      this.formSearchControl.updateValueAndValidity();
    }
    if (changes.selected != null) {
      this.selectedValues = changes.selected.currentValue || [];
    }
    if (changes.showSelected != null) {
      this.isPanelExpanded = changes.showSelected.currentValue;
    }
  }

  ngOnDestroy(): void { }

  ngAfterViewInit(): void {
    if (this.isPanelExpanded) {
      setTimeout(() => {
        this.expansionPanel.open();
        this.clearInput();
      }, 500);
    }
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value != null) {
      this.add(event.option.value);
    }

    if (this.isFilterActive) {
      this.clearInput();
      this.input.nativeElement.focus();
    } else {
      this.places = this.applyPopularFilter().sort(this.sortList);
    }

    this.changeDetectorRef.markForCheck();
  }

  private clearInput(): void {
    this.input.nativeElement.value = '';
    this.formSearchControl.setValue(null);
  }

  add(value: Destination): void {
    if (value == null) {
      return;
    }

    if (this.selectedValues == null) {
      this.selectedValues = [value];
    } else if (!this.selectedValues.includes(value)) {
      this.selectedValues = [...this.selectedValues, value];
    }
    this.changeDetectorRef.markForCheck();
  }

  remove(item: Destination): void {
    const values = [...this.selectedValues];
    const index = values.indexOf(item);
    if (index >= 0) {
      values.splice(index, 1);
      this.selectedValues = values;
    }

    if (this.selectedValues.length === 0) {
      this.panelCollapse();
    }

    this.changeDetectorRef.markForCheck();
  }

  isSelected(destination: Destination): boolean {
    return this.selectedValues != null ? this.selectedValues.indexOf(destination) >= 0 : false;
  }

  destinationText(): string {
    return this.selectedValues != null && this.selectedValues.length > 0
      ? `${(this.selectedValues[0].name)} + ${this.selectedValues.length - 1} destinations `
      : 'None selected';
  }

  panelExpanded(): void {
    this.isPanelExpanded = true;
    this.clearInput();
  }

  panelCollapse(): void {
    this.isPanelExpanded = false;
  }

  onInputKeyboardNavigation(event: KeyboardEvent): void {
    if (event.keyCode === DOWN_ARROW) {
      this.selectionList.options.first.focus();
    }
  }

  onListKeyboardNavigation(event: KeyboardEvent): void {
    if (event.keyCode === TAB) {
      this.input.nativeElement.focus();
    }
  }

  private getSelectedIds(): string[] {
    return this.selectedValues != null ? this.selectedValues.map(p => p.id) : [];
  }

  private applyFilter(value: string): Destination[] {
    const filterValue = value.toLowerCase();
    const selectedIds = this.getSelectedIds();
    return this.destinations.filter(p => !selectedIds.includes(p.id) // Remove current selection
                                    && p.destinationType !== DestinationType.Region // exclude regions
                                    && p.searchName.indexOf(filterValue) === 0);
  }

  private applyPopularFilter(): Destination[] {
    if (this.popularList == null || this.popularList.length === 0) return [];
    const selectedIds = this.getSelectedIds();

    let result = this.popularList.filter(p => !selectedIds.includes(p.id)); // Remove current selection
    const regions = new Set(result.map(p => p.destinationType !== DestinationType.Region ? p.regionId : null).filter(p => p != null)); // Get Regions

    // filter regions that do not have any countries
    result = result.filter(p => {
      if (p.destinationType === DestinationType.Region) {
        return regions.has(p.id);
      }
      return true;
    });
    return result;
  }

  private buildPopularList(values: Destination[]): Destination[] {
    const clonedList = values.map(p => ({...p}));
    const regionList = clonedList.filter(p => p.destinationType === DestinationType.Region);

    regionList.forEach(region => {
      region.sortOrder = region.sortOrder * 1000;
      let index = region.sortOrder + 1;

      const countryList = clonedList.filter(p => p.regionId === region.id).sort((a,b) => a.searchName.localeCompare(b.searchName));
      countryList.forEach(country => {
        country.sortOrder = index;
        index++;
      });
    });

    return clonedList.filter(p => p.isPopular);
  }

  private sortList(a, b) { return (a.sortOrder < b.sortOrder) ? -1 : (a.sortOrder > b.sortOrder) ? 1 : 0; }

}
